import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { motion } from "framer-motion";
import {
    BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, CartesianGrid,
} from "recharts";
import { LifeBuoy, FolderOpen, CheckCircle2, Handshake, ShieldAlert, TrendingUp } from "lucide-react";

const PIE_COLORS = ["#005EB8", "#00A499", "#FFB81C", "#DA291C", "#7C4DFF", "#94a6b8"];
const STATUS_LABELS = { new: "New", triaged: "Triaged", in_progress: "In progress", mediation: "Mediation", resolved: "Resolved", closed: "Closed" };

function StatCard({ icon: Icon, label, value, tone, i }) {
    const tones = { blue: "text-nhs-blue bg-nhs-blue/10", teal: "text-nhs-teal bg-nhs-teal/10", amber: "text-nhs-amber bg-nhs-amber/10", red: "text-nhs-red bg-nhs-red/10" };
    return (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
            className="bg-white rounded-lg border border-slate-200 shadow-sm p-6" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className={`h-11 w-11 rounded-lg flex items-center justify-center ${tones[tone]}`}><Icon className="h-5 w-5" /></div>
            <p className="mt-4 font-heading text-3xl font-extrabold text-nhs-ink">{value}</p>
            <p className="text-sm text-slate-500 mt-1">{label}</p>
        </motion.div>
    );
}

export default function Dashboard() {
    const { user } = useAuth();
    const [data, setData] = useState(null);

    useEffect(() => {
        api.get("/dashboard/stats").then((r) => setData(r.data)).catch(() => {});
    }, []);

    if (!data) return <div className="text-slate-500" data-testid="dashboard-loading">Loading dashboard…</div>;

    const isMgr = data.is_manager;
    const s = data.stats;
    const statusData = Object.entries(data.by_status || {}).map(([k, v]) => ({ name: STATUS_LABELS[k] || k, value: v }));
    const wresBar = (data.wres || []).map((w) => ({ name: w.indicator.length > 22 ? w.indicator.slice(0, 22) + "…" : w.indicator, White: w.white, BAME: w.bame }));

    return (
        <div className="space-y-8" data-testid="dashboard-page">
            <div>
                <h1 className="font-heading text-3xl font-extrabold text-nhs-ink">Welcome, {user?.name?.split(" ")[0]}</h1>
                <p className="text-slate-500 mt-1">{isMgr ? "Trust-wide equity & support overview." : "Your confidential support overview."}</p>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
                <StatCard i={0} icon={LifeBuoy} label="Total cases" value={s.total_cases} tone="blue" />
                <StatCard i={1} icon={FolderOpen} label="Open cases" value={s.open_cases} tone="amber" />
                <StatCard i={2} icon={CheckCircle2} label="Resolved" value={s.resolved_cases} tone="teal" />
                {isMgr
                    ? <StatCard i={3} icon={Handshake} label="Mediations" value={s.mediations} tone="blue" />
                    : <StatCard i={3} icon={CheckCircle2} label="Support active" value={s.open_cases > 0 ? "Yes" : "—"} tone="teal" />}
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
                <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6" data-testid="status-chart-card">
                    <h3 className="font-heading font-semibold text-lg text-nhs-ink mb-4">Cases by status</h3>
                    {statusData.length === 0 ? <p className="text-sm text-slate-400 py-16 text-center">No cases yet.</p> : (
                        <ResponsiveContainer width="100%" height={260}>
                            <PieChart>
                                <Pie data={statusData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label>
                                    {statusData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                                </Pie>
                                <Tooltip />
                            </PieChart>
                        </ResponsiveContainer>
                    )}
                </div>

                <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6" data-testid="ethnicity-chart-card">
                    <h3 className="font-heading font-semibold text-lg text-nhs-ink mb-4">Workforce ethnicity</h3>
                    <ResponsiveContainer width="100%" height={260}>
                        <BarChart data={data.ethnicity} layout="vertical" margin={{ left: 20 }}>
                            <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                            <XAxis type="number" tick={{ fontSize: 12 }} />
                            <YAxis type="category" dataKey="group" width={130} tick={{ fontSize: 11 }} />
                            <Tooltip />
                            <Bar dataKey="count" fill="#005EB8" radius={[0, 4, 4, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>

            {isMgr && (
                <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6" data-testid="wres-chart-card">
                    <div className="flex items-center gap-2 mb-4">
                        <TrendingUp className="h-5 w-5 text-nhs-blue" />
                        <h3 className="font-heading font-semibold text-lg text-nhs-ink">WRES indicators — White vs BAME</h3>
                    </div>
                    <ResponsiveContainer width="100%" height={320}>
                        <BarChart data={wresBar} margin={{ bottom: 40 }}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-20} textAnchor="end" height={60} interval={0} />
                            <YAxis tick={{ fontSize: 12 }} />
                            <Tooltip />
                            <Bar dataKey="White" fill="#94a6b8" radius={[4, 4, 0, 0]} />
                            <Bar dataKey="BAME" fill="#005EB8" radius={[4, 4, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                    <p className="text-xs text-slate-400 mt-3">Sample WRES data for demonstration. Higher BAME disciplinary and harassment values indicate priority areas.</p>
                </div>
            )}
        </div>
    );
}
